import React, { useState, useEffect } from "react";
import { Box, TextField, IconButton, Typography, Tooltip, Divider } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { useTypingAnimation } from "../../hooks/useTypingAnimation";

type MessageInputProps = {
  onSendMessage: (content: string) => void;
  onCancel: () => void;
  isGenerating: boolean;
  models: {
    [key: string]: { name: string; model: string; description: string };
  };
  selectedModel: string;
  handleModelClick: (event: React.MouseEvent<HTMLElement>) => void;
  databases: {
    [key: string]: { name: string; database: string; type: string };
  };
  selectedDatabase: string;
  handleDatabaseClick: (event: React.MouseEvent<HTMLElement>) => void;
  styles: {
    [key: string]: { name: string; style: string; type: string };
  };
  selectedStyle: string;
  handleStyleClick: (event: React.MouseEvent<HTMLElement>) => void;
  mode: string;
  suggestionPrompt: () => Promise<string[]>;
  hideDatabaseSelector?: boolean;
};

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onCancel,
  isGenerating,
  models,
  selectedModel,
  handleModelClick,
  databases,
  selectedDatabase,
  handleDatabaseClick,
  styles,
  selectedStyle,
  handleStyleClick,
  mode,
  suggestionPrompt,
  hideDatabaseSelector,
}) => {
  const [message, setMessage] = useState("");
  const theme = useTheme();

  const defaultSuggestions = [
    "ระเบียบการแต่งตั้งล่าสุด",
    "คำสั่งแต่งตั้งพนักงาน",
    "ข้อมูลจำนวนนักท่องเที่ยวต่างชาติ ของทั้งปี 2024",
  ];
  
  const [suggestions, setSuggestions] = useState<string[]>(defaultSuggestions);
  
  const animatedPlaceholder = useTypingAnimation(suggestions);

  useEffect(() => {
    const fetchSuggestions = async () => {
      const storedSuggestions = sessionStorage.getItem("suggestions");

      if (storedSuggestions) {
        setSuggestions(JSON.parse(storedSuggestions));
      } else {
        try {
          const fetchedSuggestions = await suggestionPrompt();
          const suggestionsToUse = (fetchedSuggestions && fetchedSuggestions.length > 0) 
                                    ? fetchedSuggestions 
                                    : defaultSuggestions;
          setSuggestions(suggestionsToUse);
          sessionStorage.setItem("suggestions", JSON.stringify(suggestionsToUse));
        } catch (err) {
          console.error("Error fetching suggestions:", err);
          setSuggestions(defaultSuggestions);
        }
      }
    };
    
    fetchSuggestions();
  }, [suggestionPrompt]);

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message);
      setMessage("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCancel = () => {
    onCancel();
    setMessage("");
  };

  const selectedDatabaseName =
    databases[selectedDatabase]?.name || selectedDatabase;

  return (
    <Box
      sx={{
        position: "relative",
        margin: "16px",
        "&::before": isGenerating
          ? {
              content: '""',
              position: "absolute",
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              background: `linear-gradient(90deg, rgba(255,0,0,0.4) 0%, rgba(255,154,0,0.4) 10%, rgba(208,222,33,0.4) 20%, rgba(79,220,74,0.4) 30%, rgba(63,218,216,0.4) 40%, rgba(47,201,226,0.4) 50%, rgba(28,127,238,0.4) 60%, rgba(95,21,242,0.4) 70%, rgba(186,12,248,0.4) 80%, rgba(251,7,217,0.4) 90%, rgba(255,0,0,0.4) 100%)`,
              borderRadius: "50px",
              zIndex: 0,
              animation: "rotate 4s linear infinite",
            }
          : {},
        "@keyframes rotate": {
          "0%": { filter: "hue-rotate(0deg)" },
          "100%": { filter: "hue-rotate(360deg)" },
        },
      }}
    >
      <Box
        sx={{
          background: theme.palette.mode === 'dark' 
            ? 'rgba(40, 42, 54, 0.7)' 
            : 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(16px) saturate(180%)',
          '-webkit-backdrop-filter': 'blur(16px) saturate(180%)',
          border: `1px solid rgba(255, 255, 255, 0.1)`,
          borderRadius: "50px",
          padding: "4px 8px 4px 4px",
          display: "flex",
          alignItems: "center",
          gap: 1,
          position: "relative",
          zIndex: 1,
        }}
      >
        <TextField
          fullWidth
          variant="standard"
          placeholder={animatedPlaceholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          minRows={1}
          maxRows={12}
          InputProps={{
            disableUnderline: true,
          }}
          sx={{
            flexGrow: 1,
            "& .MuiInputBase-root": {
              backgroundColor: theme.palette.background.paper,
              borderRadius: '50px',
              padding: '10px 20px',
              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
            },
            '& .MuiInputBase-input::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 1,
            },
          }}
        />
        
        {!hideDatabaseSelector && (
          <Tooltip title={selectedDatabaseName}>
            <Box
              onClick={handleDatabaseClick}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: 'center',
                flexDirection: "row",
                flexShrink: 0,
                px: 1.5
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                  mr: 0.5,
                  [theme.breakpoints.down('sm')]: {
                    maxWidth: '80px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }
                }}
              >
                {selectedDatabaseName}
              </Typography>
              <ExpandMoreOutlinedIcon
                sx={{ fontSize: 20, color: "text.secondary" }}
              />
            </Box>
          </Tooltip>
        )}

        {isGenerating ? (
          <IconButton
            onClick={handleCancel}
            sx={{
              backgroundColor: "secondary.main",
              color: "white",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              "&:hover": {
                backgroundColor: "secondary.dark",
              },
            }}
          >
            <StopIcon sx={{ fontSize: 20 }} />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleSend}
            disabled={!message.trim()}
            sx={{
              backgroundColor: message.trim() ? "primary.main" : 'action.disabledBackground',
              color: message.trim() ? "white" : 'action.disabled',
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            <SendIcon sx={{ fontSize: 20, pl: '2px' }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default MessageInput;